import { useCallback, useEffect, useMemo, useState } from 'react';
import { saveAs } from 'file-saver';
import { Box, Container, Paper } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAPI } from '../../../api/request';
import Loading from '../../../components/common/Loading.component';
import Alert from '../../../components/common/Alert.component';
import dayjs from 'dayjs';
import ToolbarComponent from '../../../components/toolbar/Toolbar.component';
import ListView from '../../../views/List.view';
import { useNavigate, useParams } from 'react-router-dom';
import _ from 'lodash';
import Button from '../../../components/common/Button.component';
import SyncButton from '../../../components/common/syncButton.component';
import CustomPopover from '../../../components/common/Popover.component';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

const ContactsListPage = ({
  module: { tag, query, list, search, filter },
  currentTabId,
}) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    data: { loading: loadingGET },
    list: { selected },
  } = useSelector((state) => state[tag]);
  const { from, to } = useSelector((state) => state.year);
  const { jwt, user } = useSelector((state) => state.login);
  const _query = useSelector(query.all.selector);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const userAuth = useSelector((state) => state.login?.user);
  const hasPermissionsRead =
    userAuth.permissions[_.toUpper(tag)]?.READ === 'true';
  const hasPermissionsDownload =
    userAuth.permissions[_.toUpper(tag)]?.DOWNLOAD_CSV === 'true';

  const filteredFilter = useMemo(() => {
    if (!filter?.conf?.fields) return null;

    if (userAuth.role === 'Administrator') {
      return filter;
    }

    return {
      ...filter,
      conf: {
        ...filter.conf,
        fields: filter.conf.fields.filter((field) => field.id !== 'blocked'),
      },
    };
  }, [filter, userAuth.role]);

  const filtersCreatedAt = useMemo(
    () => ({
      'filters[createdAt][$gte]': from,
      'filters[createdAt][$lte]': to,
    }),
    [to, from]
  );

  const syncConfigurations = useMemo(
    () => [
      {
        label: 'Sync Suppliers',
        apiSettings: query.suppliers_sync?.settings,
        confirmMessage: 'Are you sure you want to sync suppliers?',
        successMessage: 'Suppliers successfully synchronized',
        loadingMessage: 'Synchronizing suppliers...',
      },
      {
        label: 'Sync Items',
        apiSettings: query.items_sync?.settings,
        confirmMessage: 'Are you sure you want to sync items?',
        successMessage: 'Items successfully synchronized',
        loadingMessage: 'Synchronizing items...',
      },
    ],
    [query.suppliers_sync?.settings, query.items_sync?.settings]
  );

  const onError = useCallback((error) => {
    setSuccess(false);
    setLoading(false);
    setError(error);
    // eslint-disable-next-line
  }, []);

  const onSuccess = useCallback((message) => {
    setSuccess(message || true);
    setError(false);
    setLoading(false);
  }, []);

  const handleSyncSuccess = useCallback(
    (message) => {
      onSuccess(message);
    },
    [onSuccess]
  );

  const handleDownload = useCallback((resp) => {
    setError(false);
    setLoading(false);
    const csvBlob = new Blob([resp], { type: 'text/csv;charset=utf-8' });
    const nameCSV = `contacts_${dayjs().format('MM-D-YYYY_HH:mm')}.csv`;
    saveAs(csvBlob, nameCSV);
    setSuccess('Success: Download CSV')
  }, []);

  const csvQuery = useMemo(() => {
    const baseQuery = _.cloneDeep(_.omit(_query, ['pagination']));

    if (currentTabId !== 'customers') {
      return {
        ...baseQuery,
        populate: query.all.populate,
      };
    }

    const sourceOr = baseQuery?.filters?.$or || [];
    const sourceAnd = baseQuery?.filters?.$and || [];

    const yachtId = sourceAnd?.[0]?.$or?.[0]?.id_yacht?.id?.$eq;
    const crewResponsability = sourceAnd?.[1]?.crew_responsability?.$eq;
    const feedback = sourceAnd?.[2]?.id_yacht?.feedback?.$eq;
    const commercial = sourceAnd?.[3]?.id_yacht?.commercial?.$eq;

    const newOr = [
      {
        email: {
          $containsi:
            sourceOr?.[0]?.id_users_permissions_user?.email?.$containsi || '',
        },
      },
      {
        surname: {
          $containsi:
            sourceOr?.[1]?.id_users_permissions_user?.surname?.$containsi || '',
        },
      },
      {
        name: {
          $containsi:
            sourceOr?.[2]?.id_users_permissions_user?.name?.$containsi || '',
        },
      },
    ];

    const crewSearchValue =
      sourceOr?.[3]?.id_yacht_member?.crew_responsability?.$containsi ??
      sourceOr?.[3]?.crew_responsability?.$containsi;

    if (crewSearchValue !== undefined) {
      newOr.push({
        id_yacht_member: {
          crew_responsability: {
            $containsi: crewSearchValue,
          },
        },
      });
    }

    const yachtSearchValue =
      sourceOr?.[4]?.id_yacht_member?.id_yacht?.yacht_name?.$containsi ??
      sourceOr?.[4]?.id_yacht?.yacht_name?.$containsi;

    if (yachtSearchValue !== undefined) {
      newOr.push({
        id_yacht_member: {
          id_yacht: {
            yacht_name: {
              $containsi: yachtSearchValue,
            },
          },
        },
      });
    }

    const newAnd = [];

    if (yachtId !== undefined) {
      newAnd.push({
        id_yacht_member: {
          id_yacht: {
            id: {
              $eq: yachtId,
            },
          },
        },
      });
    }

    if (crewResponsability !== undefined) {
      newAnd.push({
        id_yacht_member: {
          crew_responsability: {
            $eq: crewResponsability,
          },
        },
      });
    }

    if (feedback !== undefined) {
      newAnd.push({
        id_yacht: {
          feedback: {
            $eq: feedback,
          },
        },
      });
    }

    if (commercial !== undefined) {
      newAnd.push({
        id_yacht: {
          commercial: {
            $eq: commercial,
          },
        },
      });
    }

    return {
      ...baseQuery,
      filters: {
        ...baseQuery.filters,
        $or: newOr,
        $and: newAnd,
      },
      ...filtersCreatedAt,
    };
  }, [_query, currentTabId, filtersCreatedAt]);

  const onClickDownload = useCallback(() => {
    setLoading(true);
    setError(false);
    setSuccess(false);
    fetchAPI(
      query.CSV.settings,
      {},
      csvQuery,
      {},
      jwt
    )
      .then((resp) => handleDownload(resp))
      .catch(onError);
  }, [
    query,
    jwt,
    csvQuery,
    onError,
    handleDownload,
  ]);

  const onClick = useCallback(
    (item) => {
      navigate(`${item.id}`, { state: { from: tag } });
    },
    [navigate, tag]
  );

  const getApiSettings = useCallback(() => {
    return user.role === 'Administrator'
      ? query.all.settings
      : currentTabId === 'na'
        ? query.allNotBlocked.settings
        : query.all.settings;
  }, [user.role, query, currentTabId]);

  const onSelectItem = useCallback(
    (row) => dispatch(list.actions.selected(row.id)),
    [dispatch, list]
  );

  useEffect(() => {
    const params = id ? { id } : {};
    if (hasPermissionsRead) {
      dispatch(
        query.all.action({
          settings: getApiSettings(),
          token: jwt,
          params,
          query: {
            ..._query,
            populate: query.all.populate,
            ...(currentTabId === 'customers' ? filtersCreatedAt : {}),
          },
        })
      );
    }
  }, [
    _query,
    dispatch,
    jwt,
    query,
    id,
    hasPermissionsRead,
    from,
    to,
    currentTabId,
    getApiSettings,
    success,
    filtersCreatedAt,
  ]);

  return (
    <Box
      display='flex'
      justifyContent={'end'}
      flexDirection='column'
      alignItems={'end'}
    >
      {(loading || loadingGET) && <Loading />}
      {success && (
        <Alert severity='success'>
          {typeof success === 'string'
            ? success
            : 'Operation completed successfully'}
        </Alert>
      )}
      {error && (
        <Alert severity='error'>
          {error?.response?.data?.error?.message
            ? error?.response?.data?.error.message
            : 'An error has occurred'}
        </Alert>
      )}
      <Container disableGutters={true} maxWidth={false} sx={{ height: '100%' }}>
        <Paper variant='outlined'>
          <ToolbarComponent
            tag={tag}
            search={search}
            filter={filteredFilter}
            list={list}
            add={list?.add}
            link={list?.link}
            tagPermissions={`${_.toUpper(tag)}`}
          />
          <Box sx={{ display: 'flex', justifyContent: 'end', mr: 2, my: 2 }}>
            {!!hasPermissionsDownload && currentTabId !== 'suppliers' && (
              <Button
                onClick={onClickDownload}
                variant='contained'
                size='small'
                sx={{ maxWidth: 150 }}
                startIcon={<FileDownloadIcon />}
              >
                Download CSV
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'end', mr: 2, my: 2 }}>
            {hasPermissionsDownload && currentTabId === 'suppliers' && (
              <CustomPopover
                label='Sync Actions'
                buttonProps={{
                  variant: 'outlined',
                  size: 'large',
                  sx: { minWidth: 160 },
                }}
              >
                {({ close }) =>
                  syncConfigurations.map((config, index) => (
                    <SyncButton
                      key={index}
                      label={config.label}
                      apiSettings={config.apiSettings}
                      query={{ ..._query, populate: query.all.populate }}
                      jwt={jwt}
                      onSuccess={(msg) => {
                        handleSyncSuccess(msg);
                        close();
                      }}
                      setSuccess={setSuccess}
                      setError={setError}
                      onError={onError}
                      disabled={loading}
                      confirmMessage={config.confirmMessage}
                      successMessage={config.successMessage}
                      loadingMessage={config.loadingMessage}
                      sx={{ width: '100%', justifyContent: 'flex-start' }}
                    />
                  ))
                }
              </CustomPopover>
            )}
          </Box>
          {loading && <Loading />}
          {hasPermissionsRead && (
            <ListView
              list={list}
              onClick={onClick}
              onSelectItem={onSelectItem}
              selected={selected}
            />
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default ContactsListPage;
